<template>
  <article
    class="flex flex-col gap-4 pb-8 pt-8 first:pt-0 lg:flex-row lg:items-start lg:gap-12"
  >
    <div class="min-w-[260px] max-w-[425px] space-y-3">
      <p class="font-helvetica text-[14px] font-light text-gray-light">
        {{ date }}
      </p>
      <h3
        class="font-helvetica text-[28px] leading-tight font-bold uppercase text-gray-dark"
      >
        {{ title }}
      </h3>
    </div>

    <div
      class="announcement-body min-w-0 flex-1"
      v-html="renderedBodyHtml"
    ></div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import MarkdownIt from "markdown-it";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

const props = defineProps<{
  date: string;
  title: string;
  body: string;
}>();

const normalizeText = (value: unknown) =>
  String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .trim();

const normalizeComparableText = (value: string) =>
  value
    .toLowerCase()
    .replace(/[«»"'`#*_~\-–—:;,.!?()[\]{}]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const looksLikeMarkdown = (value: string) =>
  /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+[.)]\s|>\s|```|~~~)|(^|\n)\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$|\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\[[^\]]+\]\([^)]+\)/m.test(
    value,
  );

const stripDuplicatedHeading = (body: string, title: string) => {
  const normalizedTitle = normalizeComparableText(title);
  if (!normalizedTitle) return body;

  const lines = body.split("\n");
  const firstNonEmptyIndex = lines.findIndex((line) => line.trim().length > 0);

  if (firstNonEmptyIndex < 0) return body;

  const headingMatch = lines[firstNonEmptyIndex]?.match(/^#{1,6}\s+(.*)$/);
  if (!headingMatch?.[1]) return body;

  const normalizedHeading = normalizeComparableText(headingMatch[1]);
  if (!normalizedHeading || normalizedHeading !== normalizedTitle) return body;

  lines.splice(firstNonEmptyIndex, 1);

  while (lines[firstNonEmptyIndex]?.trim() === "") {
    lines.splice(firstNonEmptyIndex, 1);
  }

  return lines.join("\n").trim();
};

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const renderPlainText = (value: string) =>
  value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map(
      (paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br />")}</p>`,
    )
    .join("");

const preparedBody = computed(() =>
  stripDuplicatedHeading(normalizeText(props.body), normalizeText(props.title)),
);

const renderedBodyHtml = computed(() => {
  const body = preparedBody.value;
  if (!body) return "";
  return looksLikeMarkdown(body) ? md.render(body) : renderPlainText(body);
});
</script>
